import { useState, useEffect, useCallback, useRef } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ScrollArea } from "@/components/ui/scroll-area";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { Upload, Trash2, Check, Image, ArrowLeft, X, FolderPlus, Folder, Music, Video } from "lucide-react";
import { Progress } from "@/components/ui/progress";
import { DotLottieReact } from "@lottiefiles/dotlottie-react";

const DEFAULT_BUCKET = "media";
const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL;

function getPublicUrl(bucket: string, path: string) {
  return `${SUPABASE_URL}/storage/v1/object/public/${bucket}/${path}`;
}

function isDotLottie(name: string) {
  return name.endsWith(".lottie");
}

function isLottieJson(name: string) {
  return name.endsWith(".json");
}

function isLottie(name: string) {
  return isDotLottie(name) || isLottieJson(name);
}

function isImage(name: string) {
  return /\.(png|jpe?g|gif|webp|svg|avif|ico)$/i.test(name);
}

function isAudio(name: string) {
  return /\.(mp3|wav|ogg|m4a|aac|flac|webm)$/i.test(name);
}

function isVideo(name: string) {
  return /\.(mp4|webm|mov|avi|mkv|m4v|ogv)$/i.test(name);
}

interface MediaFile {
  name: string;
  path: string;
  publicUrl: string;
  isLottie: boolean;
  isDotLottie: boolean;
  isImage: boolean;
  isAudio: boolean;
  isVideo: boolean;
  size?: number;
  updatedAt?: string;
}

/** Small inline Lottie preview */
function LottieThumbnail({ url, className }: { url: string; className?: string }) {
  return (
    <DotLottieReact
      src={url}
      loop
      autoplay
      className={className}
    />
  );
}

/** Format seconds to mm:ss */
function formatDuration(seconds: number): string {
  const m = Math.floor(seconds / 60);
  const s = Math.floor(seconds % 60);
  return `${m}:${s.toString().padStart(2, "0")}`;
}

/** Video thumbnail with first frame + duration overlay */
function VideoThumbnail({ url }: { url: string }) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [duration, setDuration] = useState<number | null>(null);
  const [hasThumb, setHasThumb] = useState(false);

  useEffect(() => {
    const video = document.createElement("video");
    video.crossOrigin = "anonymous";
    video.preload = "metadata";
    video.muted = true;
    video.playsInline = true;

    video.addEventListener("loadeddata", () => {
      // Seek to 0.1s for a meaningful frame
      video.currentTime = 0.1;
    });

    video.addEventListener("seeked", () => {
      const canvas = canvasRef.current;
      if (canvas) {
        canvas.width = video.videoWidth;
        canvas.height = video.videoHeight;
        const ctx = canvas.getContext("2d");
        if (ctx) {
          ctx.drawImage(video, 0, 0);
          setHasThumb(true);
        }
      }
      if (video.duration && isFinite(video.duration)) {
        setDuration(video.duration);
      }
    });

    video.addEventListener("loadedmetadata", () => {
      if (video.duration && isFinite(video.duration)) {
        setDuration(video.duration);
      }
    });

    video.src = url;
    return () => { video.src = ""; };
  }, [url]);

  return (
    <>
      <canvas ref={canvasRef} className={`max-h-full object-contain ${hasThumb ? "" : "hidden"}`} />
      {!hasThumb && <Video className="h-8 w-8 text-muted-foreground" />}
      {duration !== null && (
        <span className="absolute bottom-1 right-1 bg-black/70 text-white text-[10px] px-1 py-0.5 rounded">
          {formatDuration(duration)}
        </span>
      )}
    </>
  );
}

/** Filter which media types are shown and uploadable */
export type MediaType = "image" | "audio" | "video" | "all";

function getAcceptString(mediaType: MediaType): string {
  switch (mediaType) {
    case "image": return "image/*,.json,.lottie";
    case "audio": return "audio/*";
    case "video": return "video/*";
    default: return "image/*,.json,.lottie,audio/*,video/*";
  }
}

function fileMatchesMediaType(file: MediaFile, mediaType: MediaType): boolean {
  if (mediaType === "all") return true;
  if (mediaType === "image") return file.isImage || file.isLottie;
  if (mediaType === "audio") return file.isAudio;
  if (mediaType === "video") return file.isVideo;
  return true;
}

interface MediaPickerDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  currentUrl?: string;
  onSelect: (url: string | undefined) => void;
  /** Subfolder inside the bucket (default: "") */
  folder?: string;
  /** Storage bucket name (default: "media") */
  bucket?: string;
  /** Filter displayed/uploadable files by type (default: "all") */
  mediaType?: MediaType;
}

export default function MediaPickerDialog({
  open,
  onOpenChange,
  currentUrl,
  onSelect,
  folder = "",
  bucket = DEFAULT_BUCKET,
  mediaType = "all",
}: MediaPickerDialogProps) {
  const [files, setFiles] = useState<MediaFile[]>([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [currentFolder, setCurrentFolder] = useState(folder);
  const [folders, setFolders] = useState<string[]>([]);
  const [previewFile, setPreviewFile] = useState<MediaFile | null>(null);
  const [newFolderName, setNewFolderName] = useState("");
  const [showNewFolder, setShowNewFolder] = useState(false);

  const fetchFiles = useCallback(async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase.storage
        .from(bucket)
        .list(currentFolder, { limit: 200, sortBy: { column: "name", order: "asc" } });
      if (error) throw error;

      const mediaFiles: MediaFile[] = [];
      const subFolders: string[] = [];

      for (const item of data || []) {
        if (item.id === null) {
          subFolders.push(item.name);
        } else {
          if (item.name === ".emptyFolderPlaceholder") continue;
          const path = currentFolder ? `${currentFolder}/${item.name}` : item.name;
          mediaFiles.push({
            name: item.name,
            path,
            publicUrl: getPublicUrl(bucket, path),
            isLottie: isLottie(item.name),
            isDotLottie: isDotLottie(item.name),
            isImage: isImage(item.name),
            isAudio: isAudio(item.name),
            isVideo: isVideo(item.name),
            size: (item.metadata as any)?.size,
            updatedAt: item.updated_at,
          });
        }
      }

      setFiles(mediaFiles.filter(f => fileMatchesMediaType(f, mediaType)));
      setFolders(subFolders);
    } catch (e: any) {
      toast.error("Erreur chargement médias: " + e.message);
    } finally {
      setLoading(false);
    }
  }, [currentFolder, bucket, mediaType]);

  useEffect(() => {
    if (open) {
      setCurrentFolder(folder);
      setPreviewFile(null);
      setShowNewFolder(false);
      setNewFolderName("");
    }
  }, [open, folder]);

  useEffect(() => {
    if (open) fetchFiles();
  }, [open, fetchFiles]);

  const handleUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFiles = e.target.files;
    if (!selectedFiles || selectedFiles.length === 0) return;
    setUploading(true);
    setUploadProgress(0);

    try {
      const totalFiles = selectedFiles.length;
      for (let i = 0; i < totalFiles; i++) {
        const file = selectedFiles[i];
        const path = currentFolder ? `${currentFolder}/${file.name}` : file.name;

        // Use XMLHttpRequest for progress tracking
        await new Promise<void>((resolve, reject) => {
          const xhr = new XMLHttpRequest();
          xhr.upload.addEventListener("progress", (evt) => {
            if (evt.lengthComputable) {
              const fileProgress = (evt.loaded / evt.total) * 100;
              const overallProgress = ((i / totalFiles) * 100) + (fileProgress / totalFiles);
              setUploadProgress(Math.round(overallProgress));
            }
          });
          xhr.addEventListener("load", () => {
            if (xhr.status >= 200 && xhr.status < 300) {
              resolve();
            } else {
              reject(new Error(`Upload failed: ${xhr.statusText}`));
            }
          });
          xhr.addEventListener("error", () => reject(new Error("Network error")));

          const { data: { session } } = { data: { session: null as any } };
          // Get session for auth header
          supabase.auth.getSession().then(({ data: { session: s } }) => {
            const url = `${SUPABASE_URL}/storage/v1/object/${bucket}/${path}`;
            xhr.open("POST", url);
            xhr.setRequestHeader("Authorization", `Bearer ${s?.access_token}`);
            xhr.setRequestHeader("x-upsert", "true");
            xhr.send(file);
          });
        });
      }
      setUploadProgress(100);
      toast.success(totalFiles > 1 ? `${totalFiles} fichiers uploadés` : "Fichier uploadé");
      await fetchFiles();
    } catch (e: any) {
      toast.error("Erreur upload: " + e.message);
    } finally {
      setUploading(false);
      setUploadProgress(0);
      e.target.value = "";
    }
  };

  const handleDelete = async (file: MediaFile) => {
    if (!confirm(`Supprimer "${file.name}" ?`)) return;
    try {
      const { error } = await supabase.storage.from(bucket).remove([file.path]);
      if (error) throw error;
      toast.success("Fichier supprimé");
      if (currentUrl === file.publicUrl) {
        onSelect(undefined);
      }
      if (previewFile?.path === file.path) setPreviewFile(null);
      await fetchFiles();
    } catch (e: any) {
      toast.error("Erreur suppression: " + e.message);
    }
  };

  const handleCreateFolder = async () => {
    const name = newFolderName.trim();
    if (!name) return;
    try {
      const placeholderPath = currentFolder
        ? `${currentFolder}/${name}/.emptyFolderPlaceholder`
        : `${name}/.emptyFolderPlaceholder`;
      const { error } = await supabase.storage
        .from(bucket)
        .upload(placeholderPath, new Blob([""]), { upsert: true });
      if (error) throw error;
      toast.success(`Dossier "${name}" créé`);
      setNewFolderName("");
      setShowNewFolder(false);
      await fetchFiles();
    } catch (e: any) {
      toast.error("Erreur création dossier: " + e.message);
    }
  };

  const handleDeleteFolder = async (folderName: string) => {
    if (!confirm(`Supprimer le dossier "${folderName}" et tout son contenu ?`)) return;
    try {
      const folderPath = currentFolder ? `${currentFolder}/${folderName}` : folderName;
      const filesToDelete: string[] = [];
      const listRecursive = async (prefix: string) => {
        const { data } = await supabase.storage
          .from(bucket)
          .list(prefix, { limit: 1000 });
        for (const item of data || []) {
          const itemPath = prefix ? `${prefix}/${item.name}` : item.name;
          if (item.id === null) {
            await listRecursive(itemPath);
          } else {
            filesToDelete.push(itemPath);
          }
        }
      };
      await listRecursive(folderPath);

      if (filesToDelete.length > 0) {
        const { error } = await supabase.storage.from(bucket).remove(filesToDelete);
        if (error) throw error;
      }
      toast.success(`Dossier "${folderName}" supprimé`);
      await fetchFiles();
    } catch (e: any) {
      toast.error("Erreur suppression dossier: " + e.message);
    }
  };

  const navigateToFolder = (folderName: string) => {
    setCurrentFolder(currentFolder ? `${currentFolder}/${folderName}` : folderName);
    setPreviewFile(null);
    setShowNewFolder(false);
  };

  const navigateUp = () => {
    const parts = currentFolder.split("/").filter(Boolean);
    parts.pop();
    setCurrentFolder(parts.join("/"));
    setPreviewFile(null);
    setShowNewFolder(false);
  };

  const selectFile = (file: MediaFile) => {
    onSelect(file.publicUrl);
    onOpenChange(false);
  };

  const breadcrumb = currentFolder ? currentFolder.split("/").filter(Boolean) : [];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[80vh] flex flex-col overflow-hidden">
        <DialogHeader>
          <DialogTitle>Sélectionner un média</DialogTitle>
          <DialogDescription className="sr-only">Parcourir et sélectionner un fichier média</DialogDescription>
        </DialogHeader>

        {/* Current selection */}
        {currentUrl && (
          <div className="flex items-center gap-2 px-1 py-2 rounded-md border bg-muted/50 text-sm">
            <span className="truncate flex-1 text-muted-foreground">Sélection : {currentUrl.split("/").pop()}</span>
            <Button variant="ghost" size="sm" className="text-destructive h-7" onClick={() => { onSelect(undefined); onOpenChange(false); }}>
              <X className="h-3 w-3 mr-1" /> Retirer
            </Button>
          </div>
        )}

        {/* Breadcrumb + actions */}
        <div className="flex items-center gap-2">
          {currentFolder && (
            <Button variant="ghost" size="icon" className="h-8 w-8" onClick={navigateUp}>
              <ArrowLeft className="h-4 w-4" />
            </Button>
          )}
          <div className="flex items-center gap-1 text-sm text-muted-foreground flex-1 min-w-0">
            <span className="font-medium">{bucket}</span>
            {breadcrumb.map((part, i) => (
              <span key={i}>/ {part}</span>
            ))}
          </div>
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8"
            onClick={() => setShowNewFolder(!showNewFolder)}
            title="Nouveau dossier"
          >
            <FolderPlus className="h-4 w-4" />
          </Button>
          <label>
            <Input type="file" className="hidden" accept={getAcceptString(mediaType)} onChange={handleUpload} disabled={uploading} multiple />
            <Button variant="outline" size="sm" asChild disabled={uploading}>
              <span className="cursor-pointer">
                <Upload className="h-3.5 w-3.5 mr-1" />
                {uploading ? "Upload..." : "Upload"}
              </span>
            </Button>
          </label>
        </div>

        {/* Upload progress */}
        {uploading && (
          <div className="flex items-center gap-2 px-1">
            <Progress value={uploadProgress} className="h-2 flex-1" />
            <span className="text-xs text-muted-foreground whitespace-nowrap">{uploadProgress}%</span>
          </div>
        )}

        {/* New folder input */}
        {showNewFolder && (
          <div className="flex items-center gap-2">
            <Input
              value={newFolderName}
              onChange={(e) => setNewFolderName(e.target.value)}
              placeholder="Nom du dossier…"
              className="h-8 text-sm"
              onKeyDown={(e) => { if (e.key === "Enter") handleCreateFolder(); if (e.key === "Escape") { setShowNewFolder(false); setNewFolderName(""); } }}
              autoFocus
            />
            <Button size="sm" className="h-8" onClick={handleCreateFolder} disabled={!newFolderName.trim()}>
              Créer
            </Button>
            <Button size="sm" variant="ghost" className="h-8" onClick={() => { setShowNewFolder(false); setNewFolderName(""); }}>
              <X className="h-3.5 w-3.5" />
            </Button>
          </div>
        )}

        {/* Preview pane */}
        {previewFile && (
          <div className="rounded-md border bg-muted/30 p-3 flex flex-col items-center gap-2">
            <div className="flex w-full items-center justify-between">
              <span className="text-sm font-medium truncate">{previewFile.name}</span>
              <div className="flex gap-1">
                <Button size="sm" variant="default" className="h-7" onClick={() => selectFile(previewFile)}>
                  <Check className="h-3 w-3 mr-1" /> Sélectionner
                </Button>
                <Button size="sm" variant="ghost" className="h-7" onClick={() => setPreviewFile(null)}>
                  <X className="h-3 w-3" />
                </Button>
              </div>
            </div>
            <div className="w-full max-h-48 flex items-center justify-center overflow-hidden rounded bg-background">
              {previewFile.isLottie ? (
                <DotLottieReact
                  src={previewFile.publicUrl}
                  loop
                  autoplay
                  style={{ height: 192, width: 192 }}
                />
              ) : previewFile.isVideo ? (
                <video src={previewFile.publicUrl} controls className="max-h-48 w-full" preload="metadata" />
              ) : previewFile.isAudio ? (
                <div className="flex flex-col items-center gap-3 p-4 w-full">
                  <Music className="h-10 w-10 text-muted-foreground" />
                  <audio src={previewFile.publicUrl} controls className="w-full" preload="metadata" />
                </div>
              ) : previewFile.isImage ? (
                <img src={previewFile.publicUrl} alt={previewFile.name} className="max-h-48 object-contain" />
              ) : (
                <span className="text-muted-foreground text-sm">Aperçu non disponible</span>
              )}
            </div>
          </div>
        )}

        {/* File list */}
        <div className="min-h-0 max-h-[40vh] overflow-auto">
          {loading ? (
            <div className="p-8 text-center text-muted-foreground">Chargement…</div>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 p-1">
              {folders.map((f) => (
                <div
                  key={f}
                  className="group relative flex items-center gap-2 rounded-md border p-3 hover:bg-accent/50 transition-colors text-left cursor-pointer"
                  onClick={() => navigateToFolder(f)}
                >
                  <Folder className="h-4 w-4 text-muted-foreground shrink-0" />
                  <span className="text-sm truncate flex-1">{f}/</span>
                  <Button
                    variant="destructive"
                    size="icon"
                    className="h-6 w-6 opacity-0 group-hover:opacity-100 transition-opacity shrink-0"
                    onClick={(e) => { e.stopPropagation(); handleDeleteFolder(f); }}
                    title="Supprimer le dossier"
                  >
                    <Trash2 className="h-3 w-3" />
                  </Button>
                </div>
              ))}

              {files.map((file) => {
                const isSelected = currentUrl === file.publicUrl;
                return (
                  <div
                    key={file.path}
                    className={`group relative rounded-md border p-2 transition-colors cursor-pointer
                      ${isSelected ? "ring-2 ring-primary bg-primary/5" : "hover:bg-accent/30"}`}
                    onClick={() => setPreviewFile(file)}
                  >
                    {/* Thumbnail */}
                    <div className="h-20 flex items-center justify-center rounded bg-muted/50 mb-1.5 overflow-hidden relative">
                      {file.isImage ? (
                        <img src={file.publicUrl} alt={file.name} className="max-h-full object-contain" loading="lazy" />
                      ) : file.isLottie ? (
                        <LottieThumbnail url={file.publicUrl} className="h-20 w-20" />
                      ) : file.isVideo ? (
                        <VideoThumbnail url={file.publicUrl} />
                      ) : file.isAudio ? (
                        <Music className="h-8 w-8 text-muted-foreground" />
                      ) : (
                        <Image className="h-8 w-8 text-muted-foreground" />
                      )}
                    </div>
                    <span className="text-xs truncate block">{file.name}</span>

                    {/* Quick actions */}
                    <div className="absolute top-1 right-1 opacity-0 group-hover:opacity-100 flex gap-0.5 transition-opacity">
                      <Button
                        variant="secondary"
                        size="icon"
                        className="h-6 w-6"
                        onClick={(e) => { e.stopPropagation(); selectFile(file); }}
                        title="Sélectionner"
                      >
                        <Check className="h-3 w-3" />
                      </Button>
                      <Button
                        variant="destructive"
                        size="icon"
                        className="h-6 w-6"
                        onClick={(e) => { e.stopPropagation(); handleDelete(file); }}
                        title="Supprimer"
                      >
                        <Trash2 className="h-3 w-3" />
                      </Button>
                    </div>
                    {isSelected && (
                      <div className="absolute top-1 left-1">
                        <Check className="h-4 w-4 text-primary" />
                      </div>
                    )}
                  </div>
                );
              })}

              {files.length === 0 && folders.length === 0 && (
                <div className="col-span-full text-center text-muted-foreground py-8 text-sm">
                  Aucun fichier dans ce dossier
                </div>
              )}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
